import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { extensionGroupRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { Privileges } from 'src/app/privileges/privileges';
import { getExtensionGroupDetail } from '../queries/extension-group-detail.graphql';
import {
   GetExtensionGroupDetail,
   GetExtensionGroupDetailVariables,
} from '../queries/generated/GetExtensionGroupDetail';
import { ExtensionGroupDetailApiService } from '../services/extension-group-detail-api.service';
import * as ExtensionGroupDetailActions from './extension-group-detail.actions';
import { getExtensionGroup } from './extension-group-detail.selectors';
@Injectable()
export class ExtensionGroupDetailEffects {
   loadExtensionGroupDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            ExtensionGroupDetailActions.extensionGroupDetailLoad,
            ExtensionGroupDetailActions.saveExtensionGroupDetailSuccess,
            ExtensionGroupDetailActions.extensionGroupDetailCancel
         ),
         withLatestFrom(this.store.pipe(select(selectRouteParams))),
         concatMap(([action, params]) => {
            const id = parseInt(params.id, 10);

            return this.apiService
               .query<GetExtensionGroupDetail, GetExtensionGroupDetailVariables>({
                  query: getExtensionGroupDetail,
                  variables: { id },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute))
                  ),
                  map(([extensionGroupResult, privileges, route]) => {
                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageExtensions_Enabled,
                           extensionGroupResult.data.extensionGroup.account?.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return ExtensionGroupDetailActions.extensionGroupDetailAccessDenied();
                     }
                     return ExtensionGroupDetailActions.loadExtensionGroupDetailSuccess({
                        data: extensionGroupResult.data.extensionGroup,
                     });
                  }),
                  catchError((error) => {
                     return of(ExtensionGroupDetailActions.loadExtensionGroupDetailFailure(error));
                  })
               );
         })
      );
   });

   saveExtensionGroupDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionGroupDetailActions.saveExtensionGroupDetail),
         withLatestFrom(this.store.pipe(select(getExtensionGroup))),
         switchMap(([action, extensionGroup]) => {
            const apiCall = this.extensionGroupDetailApiService.save(
               action.extensionGroup,
               action.extensionGroupFields,
               extensionGroup
            );
            return apiCall.pipe(
               map((result) => {
                  return ExtensionGroupDetailActions.saveExtensionGroupDetailSuccess({ data: result.data });
               }),
               catchError((error) => {
                  return of(ExtensionGroupDetailActions.saveExtensionGroupDetailFailure(error));
               })
            );
         })
      );
   });

   saveExtensionGroupDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionGroupDetailActions.saveExtensionGroupDetailSuccess),
            tap(() => {
               this.snackBar.open('Extension Group Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   deleteExtensionGroup = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionGroupDetailActions.deleteExtensionGroup),
         switchMap((action) => {
            const message = 'Are you sure you would like to delete this Extension Group?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete Extension Group?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.extensionGroupDetailApiService.deleteExtensionGroup(action.extensionGroupId).pipe(
                        map(() => {
                           return ExtensionGroupDetailActions.deleteExtensionGroupSuccess(action);
                        }),
                        catchError((error) => {
                           return of(ExtensionGroupDetailActions.deleteExtensionGroupFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deleteExtensionGroupSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionGroupDetailActions.deleteExtensionGroupSuccess),
            tap((action) => {
               this.router.navigate([extensionGroupRoute]);
               this.snackBar.open(`Extension Group ${action.extensionGroupId} deleted`);
            })
         );
      },
      { dispatch: false }
   );

   dialogId: string;

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>,
      private extensionGroupDetailApiService: ExtensionGroupDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private location: Location,
      private dialogService: LcsDialogService
   ) {}
}
